import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  User,
  CreditCard,
  Lock,
  Share2,
  RefreshCw,
  Cookie,
  Mail,
} from "lucide-react";

// Data structure for sections
const privacySections = [
  {
    id: 1,
    title: "أنواع البيانات التي نقوم بجمعها",
    icon: <User className="w-7 h-7 text-emerald-500" />,
    content: [
      "المعلومات الشخصية: مثل الاسم الكامل، تاريخ الميلاد، الجنس، البريد الإلكتروني، رقم الهاتف، وبيانات الاتصال الأخرى.",
      "المعلومات الطبية الأساسية: عند حجز موعد، قد نطلب معلومات صحية ذات صلة مثل سبب الزيارة، التاريخ الطبي المختصر، والحساسيات المعروفة.",
      "معلومات الدفع: لإتمام عمليات الحجز والخدمات المدفوعة، نجمع بيانات الدفع مثل بطاقات الائتمان عبر بوابات دفع آمنة ومعتمدة.",
      "بيانات الاستخدام التقنية: مثل عنوان IP، نوع الجهاز والمتصفح، نظام التشغيل، والصفحات التي تمت زيارتها لتحسين تجربتك.",
    ],
  },
  {
    id: 2,
    title: "أغراض استخدام بياناتك",
    icon: <CreditCard className="w-7 h-7 text-emerald-500" />,
    content: [
      "إدارة وتقديم الخدمات: تمكينك من حجز المواعيد، إدارة حسابك، وتسهيل التواصل مع الأطباء.",
      "تحسين المنصة: تحليل أنماط الاستخدام، تحسين واجهة المستخدم، إضافة ميزات جديدة، وضمان أفضل أداء.",
      "التواصل معك: إرسال تأكيدات المواعيد، تذكيرات، إشعارات متعلقة بحسابك، أو تحديثات حول خدماتنا.",
      "الأمان ومنع الاحتيال: حماية المنصة والمستخدمين من أي أنشطة مشبوهة أو غير مصرح بها.",
    ],
  },
  {
    id: 3,
    title: "التزامنا بأمن معلوماتك",
    icon: <Lock className="w-7 h-7 text-emerald-500" />,
    paragraph:
      "نطبق أعلى معايير الأمن السيبراني لحماية بياناتك. يشمل ذلك التشفير القوي (SSL/TLS) لجميع الاتصالات، أنظمة تحكم دقيقة بالوصول، ومراجعات أمنية دورية لضمان سرية وسلامة المعلومات.",
  },
  {
    id: 4,
    title: "متى نشارك بياناتك مع الغير",
    icon: <Share2 className="w-7 h-7 text-emerald-500" />,
    paragraph:
      "لا نشارك بياناتك إلا في الحالات التالية: (1) بموافقتك الصريحة، (2) مع الأطباء الذين حجزت لديهم مواعيد، (3) مع مزودي خدمات موثوقين مثل بوابات الدفع، (4) استجابة لمتطلبات قانونية أو قضائية.",
  },
  {
    id: 5,
    title: "حقوقك المتعلقة بالبيانات الشخصية",
    icon: <Mail className="w-7 h-7 text-emerald-500" />,
    content: [
      " الحق في الوصول: طلب نسخة من بياناتك الشخصية التي نحتفظ بها.",
      " الحق في التصحيح: تعديل أو تحديث أي معلومات غير دقيقة.",
      " الحق في الحذف: حذف بياناتك ما لم يكن هناك التزام قانوني للاحتفاظ بها.",
      " الحق في الاعتراض: الاعتراض على معالجة بياناتك في حالات محددة مثل التسويق المباشر.",
      "لممارسة هذه الحقوق، يرجى التواصل معنا عبر البريد الإلكتروني المخصص للدعم الفني، وسنتعامل مع طلبك بما يتوافق مع القوانين.",
    ],
  },
  {
    id: 6,
    title: "ملفات تعريف الارتباط (Cookies)",
    icon: <Cookie className="w-7 h-7 text-emerald-500" />,
    paragraph:
      "نستخدم ملفات الكوكيز لتعزيز تجربتك وتحليل أداء المنصة. يمكنك التحكم في إعدادات الكوكيز من خلال متصفحك، ولكن تعطيلها قد يؤثر على بعض الوظائف.",
  },
  {
    id: 7,
    title: "تحديثات سياسة الخصوصية",
    icon: <RefreshCw className="w-7 h-7 text-emerald-500" />,
    paragraph:
      "قد نقوم بتحديث سياسة الخصوصية من وقت لآخر. سيتم إشعارك بالتغييرات الهامة عبر البريد الإلكتروني أو من خلال المنصة. استمرارك في الاستخدام بعد التحديث يعد موافقة على السياسة المعدلة.",
  },
];

const Privacy = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 120,
        damping: 12,
      },
    },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-blue-50 py-20 px-4 sm:px-6 lg:px-8">
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="max-w-6xl mx-auto bg-white shadow-2xl rounded-3xl p-8 md:p-12 lg:p-16 border border-gray-100"
      >
        {/* Header */}
        <motion.div
          variants={itemVariants}
          className="text-center mb-16 md:mb-20"
        >
          <div className="inline-flex items-center justify-center p-4 mb-6 bg-indigo-100 rounded-full shadow-md">
            <ShieldCheck className="w-10 h-10 text-indigo-600" />
          </div>
          <h1 className="text-5xl md:text-6xl font-extrabold text-gray-900 mb-6 leading-tight tracking-tight">
            سياسة الخصوصية
          </h1>
          <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed font-light">
            في <span className="font-bold text-indigo-700">منصة حكمني</span>، نولي
            خصوصية بياناتك أقصى درجات الاهتمام. توضح هذه السياسة كيفية جمعنا
            للبيانات، استخدامنا لها، طرق تخزينها، وآليات حمايتها بما يضمن أعلى
            مستويات الأمان والسرية أثناء استخدامك لخدماتنا.
          </p>
        </motion.div>

        {/* Sections */}
        <motion.div variants={containerVariants} className="space-y-12">
          {privacySections.map((section) => (
            <motion.div
              key={section.id}
              variants={itemVariants}
              className="relative p-8 bg-white border border-gray-200 rounded-2xl hover:shadow-lg transition-shadow duration-300 group"
            >
              <div className="flex items-start gap-4 mb-4">
                <div className="flex-shrink-0 p-3 bg-indigo-50 rounded-lg group-hover:bg-indigo-100 transition-colors duration-300">
                  {section.icon}
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-800 group-hover:text-indigo-700 transition-colors duration-300">
                  {section.id}. {section.title}
                </h2>
              </div>
              {section.content && (
                <ul className="list-disc pr-6 space-y-3 text-gray-700 leading-relaxed text-base md:text-lg">
                  {section.content.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              )}
              {section.paragraph && (
                <p className="text-gray-700 leading-relaxed text-base md:text-lg mt-2">
                  {section.paragraph}
                </p>
              )}
            </motion.div>
          ))}
        </motion.div>

        {/* Footer */}
        <motion.div
          variants={itemVariants}
          className="mt-20 text-center text-gray-500 text-sm md:text-base font-light border-t pt-8 border-gray-200"
        >
          <p>
            &copy; {new Date().getFullYear()} منصة حكمني – جميع الحقوق محفوظة.
            <br />
            نحن نلتزم بأعلى معايير الخصوصية والأمان لخدمتك بشكل أفضل.
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Privacy;
